import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Search, 
  Grid, 
  List, 
  Copy, 
  Trash2, 
  Edit, 
  Eye, 
  X, 
  Image,
  Video,
  FileText,
  File
} from 'lucide-react';
import { MediaFile } from './MediaUploader';
import { cn } from '../utils/cn';

interface MediaLibraryProps {
  onSelect?: (file: MediaFile) => void;
  onClose?: () => void;
  isModal?: boolean;
}

const MediaLibrary: React.FC<MediaLibraryProps> = ({ 
  onSelect, 
  onClose, 
  isModal = false 
}) => {
  const [mediaFiles, setMediaFiles] = useState<MediaFile[]>([]);
  const [filteredFiles, setFilteredFiles] = useState<MediaFile[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedType, setSelectedType] = useState<string>('all');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [selectedFile, setSelectedFile] = useState<MediaFile | null>(null);
  const [editingFile, setEditingFile] = useState<MediaFile | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null);

  // Load media files from localStorage
  useEffect(() => {
    const loadMediaFiles = () => {
      try {
        const stored = localStorage.getItem('media-library');
        if (stored) {
          const files = JSON.parse(stored);
          setMediaFiles(files);
        }
      } catch (error) {
        console.error('Error loading media files:', error);
      }
    };

    loadMediaFiles();
    
    // Listen for storage changes (from other tabs)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'media-library') {
        loadMediaFiles();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // Filter files based on search and type
  useEffect(() => {
    let filtered = mediaFiles;

    // Filter by search query
    if (searchQuery.trim()) {
      filtered = filtered.filter(file =>
        file.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        file.alt?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        file.caption?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Filter by type
    if (selectedType !== 'all') {
      filtered = filtered.filter(file => {
        if (selectedType === 'images') return file.type.startsWith('image/');
        if (selectedType === 'videos') return file.type.startsWith('video/');
        if (selectedType === 'documents') return file.type === 'application/pdf';
        return true;
      });
    }

    setFilteredFiles(filtered);
  }, [mediaFiles, searchQuery, selectedType]);

  const saveMediaFiles = (files: MediaFile[]) => {
    try {
      localStorage.setItem('media-library', JSON.stringify(files));
      setMediaFiles(files);
    } catch (error) {
      console.error('Error saving media files:', error);
    }
  };

  const deleteFile = (fileId: string) => {
    const updatedFiles = mediaFiles.filter(file => file.id !== fileId);
    saveMediaFiles(updatedFiles);
    setShowDeleteConfirm(null);
  };

  const updateFileMetadata = (fileId: string, updates: Partial<MediaFile>) => {
    const updatedFiles = mediaFiles.map(file =>
      file.id === fileId ? { ...file, ...updates } : file
    );
    saveMediaFiles(updatedFiles);
    setEditingFile(null);
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      // You could add a toast notification here
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
    }
  };

  const getFileIcon = (type: string) => {
    if (type.startsWith('image/')) return Image;
    if (type.startsWith('video/')) return Video;
    if (type === 'application/pdf') return FileText;
    return File;
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (timestamp: number): string => {
    return new Date(timestamp).toLocaleDateString();
  };

  const renderGridView = () => (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4">
      {filteredFiles.map((file) => {
        const Icon = getFileIcon(file.type);
        return (
          <motion.div
            key={file.id}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white border border-gray-200 rounded-lg overflow-hidden group hover:shadow-lg transition-shadow"
          >
            <div className="aspect-square bg-gray-100 relative">
              {file.type.startsWith('image/') ? (
                <img
                  src={file.data}
                  alt={file.alt || file.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <Icon className="w-12 h-12 text-gray-400" />
                </div>
              )}
              
              {/* Overlay actions */}
              <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-all duration-200 flex items-center justify-center opacity-0 group-hover:opacity-100">
                <div className="flex space-x-2">
                  <button
                    onClick={() => setSelectedFile(file)}
                    className="p-2 bg-white text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
                    title="Preview"
                  >
                    <Eye className="w-4 h-4" />
                  </button>
                  {onSelect && (
                    <button
                      onClick={() => onSelect(file)}
                      className="p-2 bg-[#667eea] text-white rounded-lg hover:bg-[#5a6fd8] transition-colors"
                      title="Select"
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                  )}
                  <button
                    onClick={() => setEditingFile(file)}
                    className="p-2 bg-white text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
                    title="Edit"
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setShowDeleteConfirm(file.id)}
                    className="p-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                    title="Delete"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
            
            <div className="p-3">
              <p className="text-sm font-medium text-gray-900 truncate" title={file.name}>
                {file.name}
              </p>
              <p className="text-xs text-gray-500">{formatFileSize(file.size)}</p>
            </div>
          </motion.div>
        );
      })}
    </div>
  );

  const renderListView = () => (
    <div className="space-y-2">
      {filteredFiles.map((file) => {
        const Icon = getFileIcon(file.type);
        return (
          <motion.div
            key={file.id}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="bg-white border border-gray-200 rounded-lg p-4 flex items-center space-x-4 hover:shadow-md transition-shadow"
          >
            <div className="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
              {file.type.startsWith('image/') ? (
                <img
                  src={file.data}
                  alt={file.alt || file.name}
                  className="w-full h-full object-cover rounded-lg"
                />
              ) : (
                <Icon className="w-8 h-8 text-gray-400" />
              )}
            </div>
            
            <div className="flex-1 min-w-0">
              <h3 className="font-medium text-gray-900 truncate">{file.name}</h3>
              <p className="text-sm text-gray-500">
                {formatFileSize(file.size)} • {formatDate(file.uploadedAt)}
              </p>
              {file.alt && (
                <p className="text-sm text-gray-600 truncate">Alt: {file.alt}</p>
              )}
            </div>
            
            <div className="flex items-center space-x-2">
              <button
                onClick={() => copyToClipboard(file.data)}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                title="Copy URL"
              >
                <Copy className="w-4 h-4" />
              </button>
              <button
                onClick={() => setSelectedFile(file)}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                title="Preview"
              >
                <Eye className="w-4 h-4" />
              </button>
              {onSelect && (
                <button
                  onClick={() => onSelect(file)}
                  className="px-3 py-1 bg-[#667eea] text-white rounded-lg hover:bg-[#5a6fd8] transition-colors text-sm"
                >
                  Select
                </button>
              )}
            </div>
          </motion.div>
        );
      })}
    </div>
  );

  const renderContent = () => (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">Media Library</h2>
        {isModal && onClose && (
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        )}
      </div>

      {/* Search and Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            placeholder="Search media files..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#667eea] focus:border-transparent"
          />
        </div>
        
        <div className="flex items-center space-x-2">
          <select
            value={selectedType}
            onChange={(e) => setSelectedType(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#667eea] focus:border-transparent"
          >
            <option value="all">All Types</option>
            <option value="images">Images</option>
            <option value="videos">Videos</option>
            <option value="documents">Documents</option>
          </select>
          
          <div className="flex border border-gray-300 rounded-lg">
            <button
              onClick={() => setViewMode('grid')}
              className={cn(
                "p-2 rounded-l-lg transition-colors",
                viewMode === 'grid' ? "bg-[#667eea] text-white" : "text-gray-600 hover:bg-gray-100"
              )}
            >
              <Grid className="w-4 h-4" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={cn(
                "p-2 rounded-r-lg transition-colors",
                viewMode === 'list' ? "bg-[#667eea] text-white" : "text-gray-600 hover:bg-gray-100"
              )}
            >
              <List className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Results */}
      {filteredFiles.length === 0 ? (
        <div className="text-center py-12">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Image className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No media files found</h3>
          <p className="text-gray-600">
            {searchQuery || selectedType !== 'all' 
              ? 'Try adjusting your search or filters'
              : 'Upload some files to get started'
            }
          </p>
        </div>
      ) : (
        <div>
          <p className="text-sm text-gray-600 mb-4">
            {filteredFiles.length} file{filteredFiles.length !== 1 ? 's' : ''} found
          </p>
          {viewMode === 'grid' ? renderGridView() : renderListView()}
        </div>
      )}
    </div>
  );

  return (
    <>
      {isModal ? (
        <div className="fixed inset-0 z-50 overflow-hidden">
          <div className="absolute inset-0 bg-black bg-opacity-50" onClick={onClose} />
          <motion.div
            className="relative bg-white h-full w-full max-w-6xl mx-auto shadow-xl"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.2 }}
          >
            <div className="h-full overflow-y-auto p-6">
              {renderContent()}
            </div>
          </motion.div>
        </div>
      ) : (
        renderContent()
      )}

      {/* Preview Modal */}
      <AnimatePresence>
        {selectedFile && (
          <motion.div
            className="fixed inset-0 z-50 overflow-hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <div className="absolute inset-0 bg-black bg-opacity-75" onClick={() => setSelectedFile(null)} />
            <div className="relative h-full flex items-center justify-center p-4">
              <motion.div
                className="bg-white rounded-lg max-w-4xl max-h-full overflow-hidden"
                initial={{ scale: 0.9 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0.9 }}
              >
                <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900">{selectedFile.name}</h3>
                  <button
                    onClick={() => setSelectedFile(null)}
                    className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
                <div className="p-4">
                  {selectedFile.type.startsWith('image/') ? (
                    <img
                      src={selectedFile.data}
                      alt={selectedFile.alt || selectedFile.name}
                      className="max-w-full max-h-96 object-contain mx-auto"
                    />
                  ) : selectedFile.type.startsWith('video/') ? (
                    <video
                      src={selectedFile.data}
                      controls
                      className="max-w-full max-h-96 mx-auto"
                    />
                  ) : (
                    <div className="text-center py-12">
                      <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                      <p className="text-gray-600">Preview not available for this file type</p>
                    </div>
                  )}
                </div>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Edit Modal */}
      <AnimatePresence>
        {editingFile && (
          <motion.div
            className="fixed inset-0 z-50 overflow-hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <div className="absolute inset-0 bg-black bg-opacity-50" onClick={() => setEditingFile(null)} />
            <div className="relative h-full flex items-center justify-center p-4">
              <motion.div
                className="bg-white rounded-lg max-w-md w-full"
                initial={{ scale: 0.9 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0.9 }}
                onClick={(e) => e.stopPropagation()}
              >
                <div className="p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Edit File Details</h3>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Alt Text
                      </label>
                      <input
                        type="text"
                        value={editingFile.alt || ''}
                        onChange={(e) => setEditingFile({ ...editingFile, alt: e.target.value })}
                        placeholder="Describe the image for accessibility"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#667eea] focus:border-transparent"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Caption
                      </label>
                      <textarea
                        value={editingFile.caption || ''}
                        onChange={(e) => setEditingFile({ ...editingFile, caption: e.target.value })}
                        placeholder="Optional caption for the image"
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#667eea] focus:border-transparent"
                      />
                    </div>
                  </div>
                  
                  <div className="flex justify-end space-x-3 mt-6">
                    <button
                      onClick={() => setEditingFile(null)}
                      className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => updateFileMetadata(editingFile.id, {
                        alt: editingFile.alt,
                        caption: editingFile.caption
                      })}
                      className="px-4 py-2 bg-[#667eea] text-white rounded-lg hover:bg-[#5a6fd8] transition-colors"
                    >
                      Save Changes
                    </button>
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Delete Confirmation */}
      <AnimatePresence>
        {showDeleteConfirm && (
          <motion.div
            className="fixed inset-0 z-50 overflow-hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <div className="absolute inset-0 bg-black bg-opacity-50" onClick={() => setShowDeleteConfirm(null)} />
            <div className="relative h-full flex items-center justify-center p-4">
              <motion.div
                className="bg-white rounded-lg max-w-md w-full"
                initial={{ scale: 0.9 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0.9 }}
                onClick={(e) => e.stopPropagation()}
              >
                <div className="p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Delete File</h3>
                  <p className="text-gray-600 mb-6">
                    Are you sure you want to delete this file? This action cannot be undone.
                  </p>
                  
                  <div className="flex justify-end space-x-3">
                    <button
                      onClick={() => setShowDeleteConfirm(null)}
                      className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => deleteFile(showDeleteConfirm)}
                      className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default MediaLibrary;
